import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {
  rowHeight,
  useTimelineConfig,
  useTimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {TimelineScale} from '@components/timeline-new/types'
import {cn} from '@containers/html-utils'
import {TwSwimlane} from './containers'

export const EmptySwimlane = observer(
  ({ix, path, className}: {ix: number; path: string; className?: string}) => {
    const [state] = useTimelineNewStore()
    const config = useTimelineConfig()

    const hovered = state.hoveredRowPath === path
    const top = ix * rowHeight
    const taskAddTimestamp =
      hovered && !config.static ? state.taskAddingTimestamp : undefined

    return (
      <EmptySwimlaneBase
        path={path}
        top={top}
        className={className}
        taskAddTimestamp={taskAddTimestamp}
        hovered={hovered}
      />
    )
  }
)

const EmptySwimlaneBase = observer(
  ({
    path,
    top,
    className,
    taskAddTimestamp,
    hovered
  }: {
    path: string
    top: number
    className?: string
    taskAddTimestamp?: number
    hovered?: boolean
  }) => {
    const [state, store] = useTimelineNewStore()
    const [active, setActive] = useState(false)
    useEffect(() => {
      if (active) {
        const fn = () => setActive(false)
        document.addEventListener('mouseup', fn, {capture: true})
        return () =>
          document.removeEventListener('mouseup', fn, {capture: true})
      }
      return () => void 0
    }, [active, setActive])

    return (
      <>
        <TwSwimlane
          style={{transform: `translate(0px,${top}px)`}}
          className={cn(className, taskAddTimestamp && 'cursor-pointer')}
          onClick={
            taskAddTimestamp
              ? () => store.emptySwimlaneTaskAdd(path)
              : undefined
          }
          $hovered={hovered}
          onMouseMove={e => {
            store.setHoveredRow(path)
            store.emptySwimlaneTaskAddMouseMove(path, e)
          }}
          onMouseLeave={() => store.setHoveredRow(undefined)}
          onMouseDown={() => setActive(true)}
        />
        {/* A button must be above the vertical stripes layer */}
        {taskAddTimestamp && (
          <TwSwimlane
            style={{transform: `translate(0px,${top}px)`}}
            className="pointer-events-none z-10"
          >
            <AddDeadlineButton
              left={store.timeToPosition(
                store.startTimeToSnappedTime(taskAddTimestamp)
              )}
              scale={state.scale}
              active={active}
            />
          </TwSwimlane>
        )}
      </>
    )
  }
)

const TwAddDeadlineButtonWrapper = tw.div`
  group
  absolute
  w-fit
  h-full
  flex
  items-center
`

const TwAddDeadlineButton = tw.div<{$scale: TimelineScale; $active: boolean}>`
  cursor-pointer
  rounded-4
  flex
  items-center
  justify-center
  shadow-border-1
  shadow-border-base
  bg-background-hover
  ${p => {
    const cx: string[] = []
    if (p.$active) {
      cx.push('!bg-background-active')
    }
    if (p.$scale === 'days') {
      cx.push('mx-8', 'w-24', 'h-24')
    } else if (p.$scale === 'weeks') {
      cx.push('w-24', 'h-24')
    } else if (p.$scale === 'months') {
      cx.push('w-24', 'h-24')
    } else if (p.$scale === 'years') {
      cx.push('w-6', 'h-24')
    }
    return cx.join(' ')
  }}
`

const AddDeadlineButton = ({
  left,
  scale,
  active
}: {
  left: number
  scale: TimelineScale
  active: boolean
}) => (
  <TwAddDeadlineButtonWrapper style={{transform: `translate(${left}px,0px)`}}>
    <TwAddDeadlineButton $scale={scale} $active={active}>
      {scale !== 'years' && (
        <IconTabler
          icon="IconPlus"
          className="text-icon-secondary pointer-events-none"
          customSize={16}
          viewBox="0 0 24 24"
        />
      )}
    </TwAddDeadlineButton>
  </TwAddDeadlineButtonWrapper>
)
